import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import { listCentralFillPharmaciesApi } from 'api/centralFill';
import { resolveCentralFillStateNotLicensedReview } from 'store/thunks/workQueueThunks';

const ResolveCentralFillStateModal = ({ open, onClose, lineItemId, mpi, orderState }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [pharmacy, setPharmacy] = useState(null);
  const [loading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFetching(true);
    listCentralFillPharmaciesApi({ isActive: true })
      .then((pharmacies) => setPharmacy(pharmacies?.[0] ?? null))
      .catch(() => enqueueSnackbar('Failed to load compounding pharmacy', { variant: 'error' }))
      .finally(() => setFetching(false));
  }, [open, enqueueSnackbar]);

  // Confirm action hidden for Resolve Compounding State Review
  // const handleSubmit = () => {
  //   setLoading(true);
  //   dispatch(
  //     resolveCentralFillStateNotLicensedReview({
  //       lineItemId,
  //       mpi,
  //       compoundingPharmacyId: pharmacy.compoundingPharmacyId,
  //       resolution: 'ROUTE_TO_COMPOUNDING',
  //       stateToAdd: orderState,
  //     })
  //   )
  //     .then(() => {
  //       enqueueSnackbar('State added and item routed to Compounding Queue', { variant: 'success' });
  //       onClose();
  //     })
  //     .catch(() => enqueueSnackbar('Failed to resolve compounding review', { variant: 'error' }))
  //     .finally(() => setLoading(false));
  // };

  const handleReject = () => {
    setRejecting(true);
    dispatch(
      resolveCentralFillStateNotLicensedReview({
        lineItemId,
        mpi,
        compoundingPharmacyId: pharmacy.compoundingPharmacyId,
        resolution: 'REJECT_COMPOUNDING',
      })
    )
      .then(() => {
        enqueueSnackbar('Compounding rejected — item routed to normal fulfillment', {
          variant: 'success',
        });
        onClose();
      })
      .catch(() => enqueueSnackbar('Failed to reject compounding review', { variant: 'error' }))
      .finally(() => setRejecting(false));
  };

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick') return;
    if (loading || rejecting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Resolve Compounding State Review
        <IconButton
          aria-label='close'
          onClick={onClose}
          disabled={loading || rejecting}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {fetching && <Typography variant='body2'>Loading compounding pharmacy...</Typography>}
        {!fetching && !pharmacy && (
          <Typography variant='body2' color='error'>
            No active compounding pharmacy found.
          </Typography>
        )}
        {!fetching && pharmacy && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Compounding Pharmacy
              </Typography>
              <Typography variant='body1'>{pharmacy.name}</Typography>
            </Box>

            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Currently Licensed States
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {pharmacy.licensedStates.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    No states licensed yet
                  </Typography>
                ) : (
                  pharmacy.licensedStates.map((s) => <Chip key={s} label={s} size='small' />)
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Requested Order State
              </Typography>
              <Chip label={orderState || 'N/A'} color='primary' size='small' />
            </Box>

            <Typography variant='body2' color='text.secondary'>
              <strong>Confirm</strong> adds <strong>{orderState}</strong> to the licensed states of{' '}
              <strong>{pharmacy.name}</strong> and routes this prescription to the Compounding
              Queue.
              <br />
              <strong>Reject</strong> skips compounding and routes the prescription through the
              normal fulfillment flow.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          loading={rejecting}
          onClick={handleReject}
          variant='outlined'
          color='error'
          disabled={!pharmacy || loading}
        >
          Reject
        </LoadingButton>
        {/* Confirm button hidden for Resolve Compounding State Review */}
        {/* <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          variant='contained'
          disabled={!pharmacy || !orderState || rejecting}
        >
          Confirm
        </LoadingButton> */}
      </DialogActions>
    </Dialog>
  );
};

ResolveCentralFillStateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lineItemId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  orderState: PropTypes.string,
};

ResolveCentralFillStateModal.defaultProps = {
  orderState: '',
};

export default ResolveCentralFillStateModal;
